import React, { useEffect, useState, useRef } from 'react';
import PostSaveRepairModal from '../components/repairs/PostSaveRepairModal';
import ModalPortal from '../components/ModalPortal';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import RepairFicheView from '../features/reparations/RepairFicheView';
import RepairStockPartsPanel from '../features/reparations/RepairStockPartsPanel';
import { RepFicheHeader, RepSection } from '../features/reparations/reparationsUi';
import { printTicket80mm, downloadAvoirTicketPdf } from '../utils/ticket80mmPrint';
import { getAllowedStatuses, repairStatusBadge } from '../features/reparations/reparationsUtils';
import {
  FileText, AlertCircle,
  Send, Eye, EyeOff, Trash2, Paperclip, X, Download,
} from 'lucide-react';

// Display-only mapping of backend FR status values -> i18n keys.
// The raw backend value stays the source of truth for all logic (status change,
// badge tone, allowed transitions); only the displayed label is localized.
// Unknown statuses fall back to the raw value.
const STATUS_LABEL_KEYS = {
  'Reçu': 'repairDetail.statusRecu',
  'Recu': 'repairDetail.statusRecu',
  'En Cours': 'repairDetail.statusEnCours',
  'En cours': 'repairDetail.statusEnCours',
  'Attente Pièces': 'repairDetail.statusAttentePieces',
  'Attente pieces': 'repairDetail.statusAttentePieces',
  'Pièce Arrivée': 'repairDetail.statusPieceArrivee',
  'Pièce Arrive': 'repairDetail.statusPieceArrivee',
  'Non Réparable': 'repairDetail.statusNonReparable',
  'Terminé': 'repairDetail.statusTermine',
  'Livré': 'repairDetail.statusLivre',
  'En attente de dépôt': 'repairDetail.statusAttenteDepot',
};

const RepairDetailPage = () => {
  const { ref } = useParams();
  const { api, isAdmin } = useAuth();
  const { t, tError, formatDate } = useLanguage();
  const statusLabel = (s) => (STATUS_LABEL_KEYS[s] ? t(STATUS_LABEL_KEYS[s]) : s);

  const getAttachmentUrl = (noteId, fileId) => {
    const token = localStorage.getItem('token');
    return `${process.env.REACT_APP_BACKEND_URL}/api/repair-notes/${noteId}/attachments/${fileId}?token=${token}`;
  };

  const [device, setDevice] = useState(null);
  const [notes, setNotes] = useState([]);
  const [acomptes, setAcomptes] = useState([]);
  const [avoirs, setAvoirs] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNoteText, setNewNoteText] = useState('');
  const [noteVisibility, setNoteVisibility] = useState('public');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [noteFiles, setNoteFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [printModal, setPrintModal] = useState({ open: false, defaultMode: 'ticket' });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAcompteModal, setShowAcompteModal] = useState(false);
  const [acompteData, setAcompteData] = useState({ montant: 0, mode_paiement: 'Espèces' });
  const fileInputRef = useRef(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [deviceRes, notesRes, acomptesRes, statusRes, avoirsRes] = await Promise.all([
        api.get(`/devices/${ref}`),
        api.get(`/repair-notes/${ref}`).catch(() => ({ data: [] })),
        api.get(`/acomptes?device_ref=${ref}`).catch(() => ({ data: [] })),
        api.get('/repair-statuses').catch(() => ({ data: { statuses: [] } })),
        api.get('/avoirs', { params: { device_ref: ref, limit: 20 } }).catch(() => ({ data: [] })),
      ]);
      setDevice(deviceRes.data);
      setNotes(notesRes.data || []);
      setAcomptes(acomptesRes.data || []);
      setAvoirs(Array.isArray(avoirsRes.data) ? avoirsRes.data : []);
      if (statusRes.data?.statuses?.length) setStatuses(statusRes.data.statuses);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, [ref]);

  const openPrintModal = (mode = 'ticket') => setPrintModal({ open: true, defaultMode: mode });

  const openPdfForPrint = async (endpoint) => {
    try {
      const response = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank', 'width=900,height=700');
      if (!printWindow) {
        alert(t('repairDetail.popupBlocked'));
        return;
      }
      const triggerPrint = () => { printWindow.focus(); printWindow.print(); };
      printWindow.addEventListener('load', triggerPrint);
      setTimeout(triggerPrint, 1200);
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('repairDetail.downloadError'));
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/devices/${ref}/status?status=${encodeURIComponent(newStatus)}`);
      setShowStatusModal(false);
      await fetchAll();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('repairDetail.statusUpdateError'));
    }
  };

  const handleAddAcompte = async () => {
    if (acompteData.montant <= 0) {
      alert(t('repairDetail.invalidAmount'));
      return;
    }
    try {
      await api.post('/acomptes', {
        device_ref: ref,
        montant: acompteData.montant,
        mode_paiement: acompteData.mode_paiement,
      });
      setShowAcompteModal(false);
      setAcompteData({ montant: 0, mode_paiement: 'Espèces' });
      await fetchAll();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('repairDetail.genericError'));
    }
  };

  const addNote = async () => {
    if (!newNoteText.trim() && noteFiles.length === 0) return;
    setSubmittingNote(true);
    try {
      const res = await api.post('/repair-notes', {
        device_ref: ref,
        text: newNoteText.trim() || t('repairDetail.filesAttached', { count: noteFiles.length }),
        visibility: noteVisibility,
      });
      const noteId = res.data?.id;
      if (noteId && noteFiles.length > 0) {
        for (let i = 0; i < noteFiles.length; i++) {
          setUploadProgress(t('repairDetail.uploadProgress', { current: i + 1, total: noteFiles.length }));
          const formData = new FormData();
          formData.append('file', noteFiles[i]);
          await api.post(`/repair-notes/${noteId}/attachments`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
          });
        }
      }
      setNewNoteText('');
      setNoteFiles([]);
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      const notesRes = await api.get(`/repair-notes/${ref}`);
      setNotes(notesRes.data || []);
    } catch (err) {
      alert(err?.response?.data?.detail || t('repairDetail.addNoteError'));
    }
    setSubmittingNote(false);
    setUploadProgress(null);
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm(t('repairDetail.confirmDeleteNote'))) return;
    try {
      await api.delete(`/repair-notes/${noteId}`);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (err) {
      alert(err?.response?.data?.detail || t('repairDetail.genericError'));
    }
  };

  const allowedStatuses = device
    ? getAllowedStatuses(device.status, statuses, isAdmin)
    : [];

  if (loading) {
    return (
      <Layout>
        <div className="max-w-[1400px] mx-auto py-20 flex justify-center">
          <div className="loading-spinner" />
        </div>
      </Layout>
    );
  }

  if (!device) {
    return (
      <Layout>
        <div className="max-w-[1400px] mx-auto py-20 flex flex-col items-center gap-4">
          <AlertCircle className="w-12 h-12 text-red-400" />
          <p className="text-gray-400">{t('repairDetail.notFound')}</p>
          <Link to="/reparations" className="text-red-400 hover:underline font-semibold">{t('repairDetail.backToRepairs')}</Link>
        </div>
      </Layout>
    );
  }

  const handleRepairLinesUpdated = (data) => {
    if (!data) return;
    setDevice((prev) => ({
      ...prev,
      repair_lines: data.lines || prev?.repair_lines || [],
      pricing_mode: data.pricing_mode ?? prev?.pricing_mode,
      quoted_total: data.quoted_total ?? prev?.quoted_total,
      price_repair: data.price_repair ?? prev?.price_repair,
      price_delta: data.price_delta ?? prev?.price_delta,
      reste_a_payer: data.reste_a_payer ?? prev?.reste_a_payer,
      total_acomptes: data.total_acomptes ?? prev?.total_acomptes,
      remise: data.remise ?? prev?.remise,
    }));
    fetchAll();
  };

  const repairStockSlot = device ? (
    <RepairStockPartsPanel
      api={api}
      deviceRef={ref}
      magasinId={device.magasin_id}
      lines={device.repair_lines || []}
      pricingMode={device.pricing_mode}
      quotedTotal={device.quoted_total}
      priceRepair={device.price_repair}
      priceDelta={device.price_delta}
      remise={device.remise}
      clientEmail={device.client_email}
      onUpdated={handleRepairLinesUpdated}
    />
  ) : null;

  const photos = device.photos || [];

  const photosSlot = photos.length > 0 ? (
    <RepSection title={t('repairDetail.photosTitle', { count: photos.length })} testId="photos-card">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((photo, i) => (
          <a
            key={i}
            href={`${process.env.REACT_APP_BACKEND_URL}${photo}`}
            target="_blank"
            rel="noreferrer"
            className="block aspect-square rounded-lg overflow-hidden border border-white/10 hover:border-red-500/40 transition-colors"
          >
            <img src={`${process.env.REACT_APP_BACKEND_URL}${photo}`} alt={t('repairDetail.photoAlt', { index: i + 1 })} className="w-full h-full object-cover" loading="lazy" />
          </a>
        ))}
      </div>
    </RepSection>
  ) : null;

  const notesSlot = (
    <RepSection title={t('repairDetail.notesTitle', { count: notes.length })} testId="notes-card">
      <div className="mb-4 p-3 rounded-lg bg-black/25 border border-white/10" data-testid="add-note-form">
        <textarea
          value={newNoteText}
          onChange={(e) => setNewNoteText(e.target.value)}
          placeholder={t('repairDetail.notePlaceholder')}
          className="w-full rounded-lg bg-black/30 border border-white/10 text-white text-sm py-2.5 px-3 resize-none focus:outline-none focus:border-red-500/40"
          rows={3}
          data-testid="note-text-input"
        />
        {noteFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {noteFiles.map((file, i) => (
              <div key={i} className="relative">
                <div className="w-16 h-16 rounded-lg overflow-hidden border border-white/10 bg-black/40 flex items-center justify-center">
                  {file.type.startsWith('image/') ? (
                    <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <FileText className="w-5 h-5 text-gray-500" />
                  )}
                </div>
                <button type="button" onClick={() => setNoteFiles((p) => p.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-600 text-white flex items-center justify-center">
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
        {uploadProgress && <p className="text-xs text-gray-400 mt-2">{uploadProgress}</p>}
        <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setNoteVisibility(noteVisibility === 'public' ? 'internal' : 'public')}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border ${
                noteVisibility === 'public'
                  ? 'bg-white/5 text-gray-300 border-white/10'
                  : 'bg-amber-950/30 text-amber-300 border-amber-500/25'
              }`}
            >
              {noteVisibility === 'public' ? <Eye size={12} className="inline mr-1" /> : <EyeOff size={12} className="inline mr-1" />}
              {noteVisibility === 'public' ? t('repairDetail.visibilityPublic') : t('repairDetail.visibilityInternal')}
            </button>
            <input type="file" ref={fileInputRef} onChange={(e) => setNoteFiles((p) => [...p, ...Array.from(e.target.files || [])])} multiple accept="image/*,video/*" className="hidden" />
            <button type="button" onClick={() => fileInputRef.current?.click()} className="px-2.5 py-1.5 rounded-lg text-xs font-medium border bg-white/5 text-gray-300 border-white/10">
              <Paperclip size={12} className="inline mr-1" /> {t('repairDetail.filesBtn')}
            </button>
          </div>
          <button
            type="button"
            onClick={addNote}
            disabled={(!newNoteText.trim() && noteFiles.length === 0) || submittingNote}
            className="px-4 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 text-sm font-semibold text-white disabled:opacity-40 flex items-center gap-1.5"
            data-testid="submit-note-btn"
          >
            <Send size={14} /> {submittingNote ? t('repairDetail.sending') : t('repairDetail.add')}
          </button>
        </div>
      </div>
      {notes.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-2">{t('repairDetail.noNotes')}</p>
      ) : (
        <div className="space-y-2">
          {notes.map((note) => (
            <div key={note.id} className="p-3 rounded-lg border bg-black/20 border-white/10" data-testid={`note-${note.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-xs font-bold text-gray-300">{note.author || t('repairDetail.staffDefault')}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-500">
                    {formatDate(note.created_at, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <button type="button" onClick={() => deleteNote(note.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-note-${note.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-200 whitespace-pre-wrap">{note.text}</p>
              {note.attachments?.filter((a) => (a.content_type || '').startsWith('image/')).length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {note.attachments.filter((a) => (a.content_type || '').startsWith('image/')).map((att, i) => (
                    <button
                      key={att.id || i}
                      type="button"
                      onClick={() => setLightbox({ url: getAttachmentUrl(note.id, att.id || att.file_id), filename: att.filename })}
                      className="aspect-square rounded-lg overflow-hidden border border-white/10"
                    >
                      <img src={getAttachmentUrl(note.id, att.id || att.file_id)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
              {note.attachments?.filter((a) => (a.content_type || '').startsWith('video/')).map((att, i) => (
                <div key={att.id || i} className="mt-2 rounded-lg overflow-hidden border border-white/10">
                  <video controls preload="metadata" className="w-full max-h-48 bg-black">
                    <source src={getAttachmentUrl(note.id, att.id || att.file_id)} type={att.content_type} />
                  </video>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </RepSection>
  );

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto fade-in px-1 pb-8">
        <RepFicheHeader
          refCode={ref}
          device={device}
          onPrint={() => openPrintModal('ticket')}
        />
        <RepairFicheView
          device={device}
          acomptes={acomptes}
          onStatusClick={() => setShowStatusModal(true)}
          onAddAcompte={() => setShowAcompteModal(true)}
          onPrintAcompte={(id) => printTicket80mm(api, { type: 'acompte', ref: id }).catch((error) => {
            alert(tError(error.response?.data?.detail) || t('repairDetail.printAcompteError'));
          })}
          avoirs={avoirs}
          onPrintAvoir={(refAvoir) => printTicket80mm(api, { type: 'avoir', ref: refAvoir }).catch((error) => {
            alert(tError(error.response?.data?.detail) || t('repairDetail.printAvoirError'));
          })}
          onDownloadAvoirPdf={(refAvoir) => downloadAvoirTicketPdf(api, refAvoir).catch((error) => {
            alert(tError(error.response?.data?.detail) || error.message || t('repairDetail.downloadPdfError'));
          })}
          photosSlot={photosSlot}
          notesSlot={notesSlot}
          repairStockSlot={repairStockSlot}
        />
      </div>

      <PostSaveRepairModal
        open={printModal.open}
        deviceRef={ref}
        defaultMode={printModal.defaultMode}
        onClose={() => setPrintModal({ open: false, defaultMode: 'ticket' })}
      />

      <ModalPortal isOpen={showStatusModal}>
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowStatusModal(false)}>
          <div className="bg-[#151820] border border-white/10 rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-white mb-1">{t('repairDetail.changeStatus')}</h3>
            <p className="text-sm text-gray-400 mb-4">{device.ref} · {device.model}</p>
            <div className="flex flex-wrap gap-2">
              {allowedStatuses.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => handleStatusChange(s)}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold border ${repairStatusBadge(s).className} hover:brightness-110`}
                >
                  {statusLabel(s)}
                </button>
              ))}
            </div>
            {allowedStatuses.length === 0 && (
              <p className="text-sm text-gray-500">{t('repairDetail.noStatusChange')}</p>
            )}
          </div>
        </div>
      </ModalPortal>

      <ModalPortal isOpen={showAcompteModal}>
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowAcompteModal(false)}>
          <div className="bg-[#151820] border border-white/10 rounded-2xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-white">{t('repairDetail.addDeposit')}</h3>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('repairDetail.amountLabel')}</label>
              <input
                type="number"
                value={acompteData.montant}
                onChange={(e) => setAcompteData({ ...acompteData, montant: parseFloat(e.target.value) || 0 })}
                className="w-full rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2.5"
                step="0.01"
                min="0"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('repairDetail.paymentMode')}</label>
              <select
                value={acompteData.mode_paiement}
                onChange={(e) => setAcompteData({ ...acompteData, mode_paiement: e.target.value })}
                className="w-full rounded-lg bg-black/30 border border-white/10 text-white px-3 py-2.5"
              >
                {/* value= preserves the backend FR payment-method code; only the label is localized */}
                <option value="Espèces">{t('repairDetail.paymentCash')}</option>
                <option value="CB">{t('repairDetail.paymentCard')}</option>
                <option value="Virement">{t('repairDetail.paymentTransfer')}</option>
                <option value="Chèque">{t('repairDetail.paymentCheck')}</option>
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setShowAcompteModal(false)} className="flex-1 py-2.5 rounded-lg bg-white/10 text-gray-300 font-semibold">{t('repairDetail.cancel')}</button>
              <button type="button" onClick={handleAddAcompte} className="flex-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-white font-semibold">{t('repairDetail.add')}</button>
            </div>
          </div>
        </div>
      </ModalPortal>

      {lightbox && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.filename} className="max-w-full max-h-[85vh] object-contain rounded-lg" />
            <div className="absolute top-2 right-2 flex gap-2">
              <a href={lightbox.url} download={lightbox.filename} className="p-2 rounded-full bg-black/60 text-white"><Download size={18} /></a>
              <button type="button" onClick={() => setLightbox(null)} className="p-2 rounded-full bg-black/60 text-white"><X size={18} /></button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default RepairDetailPage;
